import React from "react";
import { type BrandDefinition, type BrandKey, GREETING_MAX_LENGTH } from "@school-sales-ai/shared";

type Props = {
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
  /** The client's brand — picks the example and what `{businessName}` is called. */
  brand: BrandDefinition;
};

// The token is `{businessName}` for every brand; only how the screen DESCRIBES it
// follows the brand, so a Loxago business is not told its greeting names "la
// escuela". A total Record, so a new brand fails to compile until it has its copy.
const GREETING_COPY: Readonly<Record<BrandKey, { example: string; businessNoun: string }>> = {
  scholaops: {
    example:
      "¡Hola! 👋 Soy {agentName}, tu asistente de admisiones del {businessName}. ¿En qué puedo ayudarte hoy?",
    businessNoun: "el de la escuela",
  },
  // Loxago — `general` is the brand's internal key (packages/shared brand.ts).
  general: {
    example: "¡Hola! 👋 Soy {agentName}, tu asistente de {businessName}. ¿En qué puedo ayudarte hoy?",
    businessNoun: "el de tu negocio",
  },
};

export function GreetingSettings({ value, onChange, invalid, brand }: Props): React.JSX.Element {
  const copy = GREETING_COPY[brand.key];
  return (
    <div className="border-t border-ink-200 pt-4 mt-4">
      <div className="mb-1">
        <p className="text-[13px] font-medium text-ink-900">Saludo inicial del widget</p>
        <p className="text-[12px] text-ink-500 mt-0.5">
          El primer mensaje que ve el prospecto al abrir el chat. Déjalo vacío para usar el
          saludo por defecto.
        </p>
      </div>

      <textarea
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={GREETING_MAX_LENGTH}
        placeholder={copy.example}
        className={`mt-3 w-full resize-none text-[13px] border rounded-lg px-3 py-1.5 focus:outline-none text-ink-900 placeholder:text-ink-300 bg-white ${
          invalid ? "border-red-400 focus:border-red-500" : "border-ink-200 focus:border-moss"
        }`}
      />
      <div className="flex items-start justify-between gap-3 mt-1">
        <p className="text-[11px] text-ink-500">
          Puedes usar{" "}
          <code className="rounded bg-ink-050 px-1 text-ink-700">{"{agentName}"}</code> y{" "}
          <code className="rounded bg-ink-050 px-1 text-ink-700">{"{businessName}"}</code>; se
          reemplazan por el nombre del agente y {copy.businessNoun}.
        </p>
        <span className="text-[11px] text-ink-400 shrink-0">
          {value.length}/{GREETING_MAX_LENGTH}
        </span>
      </div>
      {invalid && (
        <p className="text-[11px] text-red-500 mt-1">
          Solo se permiten {"{agentName}"} y {"{businessName}"}.
        </p>
      )}
    </div>
  );
}
